import React, { useRef } from 'react';
import Moveable, { OnDrag, OnDragGroup, OnResize, OnResizeGroup, OnRotate, OnRotateGroup } from 'react-moveable';
import Selecto from 'react-selecto';
import { useAppDispatch, useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { updateElement, updateElements, removeElements } from '../store/formDocumentSlice';
import { setSelection } from '../store/formDesignerUiSlice';

interface Props {
  canvasRef: React.RefObject<HTMLDivElement>;
  pageId: string;
  zoom: number;
}

export default function SelectionLayer({ canvasRef, pageId, zoom }: Props) {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const page = useAppSelector((s) => s.FormDocument.present.pages.find((p) => p.id === pageId));
  const moveableRef = useRef<Moveable>(null);
  const selectoRef = useRef<Selecto>(null);

  const targets = selectedIds
    .map((id) => canvasRef.current?.querySelector<HTMLElement>(`[data-element-id="${id}"]`))
    .filter((el): el is HTMLElement => !!el);

  const isLockedSelection = selectedIds.length === 0 || selectedIds.some((id) => {
    const el = canvasRef.current?.querySelector<HTMLElement>(`[data-element-id="${id}"]`);
    return !el || el.dataset.locked === 'true';
  });

  return (
    <>
      <Selecto
        ref={selectoRef}
        container={canvasRef.current}
        dragContainer={canvasRef.current}
        selectableTargets={['.fd-element:not(.fd-element-locked)']}
        hitRate={0}
        selectByClick
        selectFromInside={false}
        toggleContinueSelect={['shift']}
        ratio={0}
        onDragStart={(e) => {
          const target = e.inputEvent.target as HTMLElement;
          // Selecto's own drag-detection isn't scoped to `container`/`dragContainer` for the
          // initiating pointerdown — without this check it also reacts to clicks anywhere else
          // in the app (e.g. the Properties Panel), firing onSelectEnd with an empty selection
          // and yanking the panel's contents (and whatever button was just clicked) out from
          // under the click before the browser can dispatch mouseup/click to it.
          if (!canvasRef.current || !canvasRef.current.contains(target)) {
            e.stop();
            return;
          }
          if (moveableRef.current?.isMoveableElement(target) || target.closest('.fd-element-locked')) {
            e.stop();
          }
        }}
        onSelectEnd={(e) => {
          // onDragStart's e.stop() only skips Selecto's bookkeeping — this
          // lifecycle callback still fires regardless, so it needs the same canvas-scoping
          // guard or it ends up dispatching an empty selection (and preventDefault-ing the
          // native event, suppressing the follow-up click) for interactions outside the canvas.
          const target = e.inputEvent.target as HTMLElement;
          if (!canvasRef.current || !canvasRef.current.contains(target)) return;

          const ids = e.selected.map((el) => (el as HTMLElement).dataset.elementId).filter(Boolean) as string[];
          dispatch(setSelection(ids));
          if (e.isDragStart && ids.length > 0) {
            e.inputEvent.preventDefault();
            setTimeout(() => moveableRef.current?.dragStart(e.inputEvent));
          }
        }}
      />
      {targets.length > 0 && !isLockedSelection && (
        <Moveable
          ref={moveableRef}
          target={targets}
          zoom={1 / zoom}
          draggable
          resizable
          rotatable
          snappable
          preventDefault={false}
          preventClickEventOnDrag={false}
          keepRatio={false}
          throttleDrag={0}
          throttleResize={0}
          throttleRotate={0}
          origin={false}
          renderDirections={targets.length > 1 ? [] : ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
          onDrag={(e: OnDrag) => {
            const id = (e.target as HTMLElement).dataset.elementId;
            if (!id) return;
            dispatch(updateElement({ pageId, id, changes: { x: e.left, y: e.top } }));
          }}
          onDragEnd={(e) => {
            const id = (e.target as HTMLElement).dataset.elementId;
            if (!id || !page) return;
            const element = page.elements.find((el) => el.id === id);
            if (!element) return;

            const EMBEDDABLE_TYPES = new Set(['text', 'label', 'paragraph', 'heading', 'pagenumber', 'textbox', 'textarea', 'checkbox', 'radio']);
            if (!EMBEDDABLE_TYPES.has(element.type)) return;

            // Find all tables on the page
            const tables = page.elements.filter((el) => el.type === 'table');
            const elCenterX = element.x + element.width / 2;
            const elCenterY = element.y + element.height / 2;

            const targetTable = tables.find((t) => {
              return elCenterX >= t.x && elCenterX <= (t.x + t.width) &&
                     elCenterY >= t.y && elCenterY <= (t.y + t.height);
            });

            if (targetTable) {
              const tProps = targetTable.typeProps?.table;
              if (tProps) {
                // Calculate relative coordinates
                const relX = elCenterX - targetTable.x;
                const relY = elCenterY - targetTable.y;

                // Find column
                let accumX = 0;
                let colIdx = 0;
                for (let c = 0; c < tProps.cols; c++) {
                  const colWidthPx = (tProps.colWidths[c] / 100) * targetTable.width;
                  if (relX >= accumX && relX <= (accumX + colWidthPx)) {
                    colIdx = c;
                    break;
                  }
                  accumX += colWidthPx;
                }

                // Find row
                let accumY = 0;
                let rowIdx = 0;
                for (let r = 0; r < tProps.rows; r++) {
                  const rowHeightPx = tProps.rowHeights[r] || 40;
                  if (relY >= accumY && relY <= (accumY + rowHeightPx)) {
                    rowIdx = r;
                    break;
                  }
                  accumY += rowHeightPx;
                }

                // Construct runs to write to cell
                let runs: any[] = [];
                if (element.bindings?.dynamicField) {
                  runs = [{ type: 'text', value: `$${element.bindings.dynamicField}$` }];
                } else if (element.type === 'checkbox') {
                  runs = [{ type: 'text', value: `☐ ${element.typeProps?.checkbox?.label || 'Checkbox'}` }];
                } else if (element.type === 'radio') {
                  runs = [{ type: 'text', value: `○ ${element.typeProps?.radio?.label || 'Option'}` }];
                } else if (element.type === 'textbox' || element.type === 'textarea') {
                  runs = [{ type: 'text', value: '[Text Box]' }];
                } else if (element.content && element.content.length > 0) {
                  runs = element.content;
                } else {
                  runs = [{ type: 'text', value: '' }];
                }

                const existingCellRuns = tProps.cells[rowIdx]?.[colIdx] || [];
                const updatedCells = tProps.cells.map((r: any, ri: number) =>
                  ri === rowIdx
                    ? r.map((c: any, ci: number) => (ci === colIdx ? [...existingCellRuns, ...runs] : c))
                    : r
                );

                const updatedTable = {
                  ...tProps,
                  cells: updatedCells,
                };

                dispatch(
                  updateElement({
                    pageId,
                    id: targetTable.id,
                    changes: { typeProps: { ...targetTable.typeProps, table: updatedTable } },
                  })
                );

                dispatch(removeElements({ pageId, ids: [element.id] }));
                dispatch(setSelection([]));
              }
            }
          }}
          onDragGroup={(e: OnDragGroup) => {
            const changes = e.events
              .map((ev) => {
                const id = (ev.target as HTMLElement).dataset.elementId;
                return id ? { id, changes: { x: ev.left, y: ev.top } } : null;
              })
              .filter(Boolean) as { id: string; changes: any }[];
            dispatch(updateElements({ pageId, changes }));
          }}
          onResize={(e: OnResize) => {
            const id = (e.target as HTMLElement).dataset.elementId;
            if (!id) return;
            e.target.style.width = `${e.width}px`;
            e.target.style.height = `${e.height}px`;
            dispatch(
              updateElement({
                pageId,
                id,
                changes: { width: e.width, height: e.height, x: e.drag.left, y: e.drag.top },
              })
            );
          }}
          onResizeGroup={(e: OnResizeGroup) => {
            const changes = e.events
              .map((ev) => {
                const id = (ev.target as HTMLElement).dataset.elementId;
                if (!id) return null;
                ev.target.style.width = `${ev.width}px`;
                ev.target.style.height = `${ev.height}px`;
                return {
                  id,
                  changes: { width: ev.width, height: ev.height, x: ev.drag.left, y: ev.drag.top },
                };
              })
              .filter(Boolean) as { id: string; changes: any }[];
            dispatch(updateElements({ pageId, changes }));
          }}
          onRotate={(e: OnRotate) => {
            const id = (e.target as HTMLElement).dataset.elementId;
            if (!id) return;
            dispatch(updateElement({ pageId, id, changes: { rotation: e.rotation } }));
          }}
          onRotateGroup={(e: OnRotateGroup) => {
            const changes = e.events
              .map((ev) => {
                const id = (ev.target as HTMLElement).dataset.elementId;
                return id ? { id, changes: { rotation: ev.rotation } } : null;
              })
              .filter(Boolean) as { id: string; changes: any }[];
            dispatch(updateElements({ pageId, changes }));
          }}
        />
      )}
    </>
  );
}
